import { AlertCircle, Minus, Plus, Sparkles, Tag, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ZonePicker } from '@/components/delivery/zone-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState } from '@/components/ui/states';
import {
  useApplyCoupon,
  useCart,
  useRemoveCartItem,
  useRemoveCoupon,
  useUpdateCartItem,
} from '@/hooks/useCart';
import { cn, formatPrice } from '@/lib/utils';
import type { Cart, CartItem } from '@/types/cart';

/**
 * SEPET İÇERİĞİ
 *
 * Aynı içerik iki kabukta kullanılır: masaüstünde sağdan drawer,
 * mobilde tam sayfa. Fiyatların tamamı backend'den gelir; burada
 * hiçbir toplama yapılmaz.
 */
export function CartContents({ onNavigate }: { onNavigate?: () => void }) {
  const { data: cart, isPending, isError, error, refetch } = useCart();

  if (isPending) {
    return <CartSkeleton />;
  }

  if (isError) {
    return <ErrorState error={error} onRetry={() => void refetch()} />;
  }

  if (cart.items.length === 0) {
    return (
      <EmptyState
        icon={<Sparkles className="size-6" aria-hidden />}
        title="Sepetiniz henüz boş"
        description="Beğendiğiniz çiçekleri sepete ekleyerek devam edebilirsiniz."
        action={{ label: 'Çiçekleri keşfet', to: '/koleksiyon/cok-satanlar' }}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <ul className="divide-y divide-line">
          {cart.items.map((item) => (
            <CartLine key={item.id} item={item} onNavigate={onNavigate} />
          ))}
        </ul>

        {cart.suggestedAddons.length > 0 ? (
          <SuggestedAddons cart={cart} />
        ) : null}

        <div className="border-t border-line px-4 py-5 sm:px-5">
          <CouponField cart={cart} />
        </div>
      </div>

      <CartSummary cart={cart} onNavigate={onNavigate} />
    </div>
  );
}

// ---------------------------------------------------------------------------

function CartLine({
  item,
  onNavigate,
}: {
  item: CartItem;
  onNavigate?: () => void;
}) {
  const update = useUpdateCartItem();
  const remove = useRemoveCartItem();

  return (
    <li className="flex gap-3.5 px-4 py-5 sm:px-5">
      <Link
        to={`/urun/${item.product.slug}`}
        onClick={onNavigate}
        className="shrink-0"
      >
        {item.product.imageUrl ? (
          <img
            src={item.product.imageUrl}
            alt=""
            aria-hidden
            loading="lazy"
            className="size-20 rounded-sm bg-surface-sunken object-cover sm:size-24"
          />
        ) : (
          <span className="block size-20 rounded-sm bg-surface-sunken sm:size-24" />
        )}
      </Link>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={`/urun/${item.product.slug}`}
              onClick={onNavigate}
              className="font-serif text-base leading-snug text-ink transition-colors hover:text-leaf-800"
            >
              {item.product.name}
            </Link>

            {item.variant ? (
              <p className="mt-0.5 text-xs text-ink-muted">{item.variant.label}</p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => remove.mutate(item.id)}
            disabled={remove.isPending}
            aria-label={`${item.product.name} ürününü sepetten kaldır`}
            className="-mr-1 flex size-8 shrink-0 items-center justify-center rounded-sm text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-danger"
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        </div>

        {item.addons.length > 0 ? (
          <ul className="mt-2 space-y-0.5 border-l-2 border-line pl-2.5">
            {item.addons.map((addon) => (
              <li
                key={addon.id}
                className="flex justify-between gap-3 text-xs text-ink-muted"
              >
                <span className="truncate">
                  {addon.name}
                  {addon.quantity > 1 ? ` ×${addon.quantity}` : ''}
                </span>
                <span data-price>{formatPrice(addon.lineTotal)}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {item.issue ? (
          <p className="mt-2 flex items-start gap-1.5 text-xs text-danger">
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {item.issue}
          </p>
        ) : null}

        <div className="mt-3 flex items-center justify-between gap-3">
          <div
            className="flex items-center rounded-sm border border-line"
            role="group"
            aria-label="Adet"
          >
            <button
              type="button"
              onClick={() =>
                update.mutate({ itemId: item.id, quantity: item.quantity - 1 })
              }
              disabled={item.quantity <= 1 || update.isPending}
              aria-label="Adet azalt"
              className="flex size-8 items-center justify-center text-ink-muted transition-colors hover:text-ink disabled:opacity-40"
            >
              <Minus className="size-3.5" aria-hidden />
            </button>

            <span
              className="w-8 text-center text-sm tabular-nums text-ink"
              aria-live="polite"
            >
              {item.quantity}
            </span>

            <button
              type="button"
              onClick={() =>
                update.mutate({ itemId: item.id, quantity: item.quantity + 1 })
              }
              disabled={update.isPending}
              aria-label="Adet artır"
              className="flex size-8 items-center justify-center text-ink-muted transition-colors hover:text-ink disabled:opacity-40"
            >
              <Plus className="size-3.5" aria-hidden />
            </button>
          </div>

          <span className="text-base font-medium text-ink" data-price>
            {formatPrice(item.itemTotal)}
          </span>
        </div>
      </div>
    </li>
  );
}

function SuggestedAddons({ cart }: { cart: Cart }) {
  const update = useUpdateCartItem();

  return (
    <section className="border-t border-line px-4 py-5 sm:px-5">
      <h3 className="font-serif text-base text-ink">Bu siparişi daha özel yapın</h3>

      <ul className="rail mt-3 gap-2.5">
        {cart.suggestedAddons.map((addon) => {
          const item = cart.items.find((entry) => entry.id === addon.cartItemId);

          return (
            <li key={addon.id} className="w-[34%] min-[420px]:w-[28%]">
              <button
                type="button"
                disabled={update.isPending || !item}
                onClick={() => {
                  if (!item) {
                    return;
                  }

                  // Mevcut ek ürünler korunur, yenisi eklenir (duplicate yok).
                  update.mutate({
                    itemId: item.id,
                    addons: [
                      ...item.addons.map((entry) => ({
                        addonId: entry.addonId,
                        quantity: entry.quantity,
                      })),
                      { addonId: addon.id, quantity: 1 },
                    ],
                  });
                }}
                className="group w-full text-left disabled:opacity-50"
              >
                <span className="relative block overflow-hidden rounded-sm border border-line transition-colors group-hover:border-leaf-300">
                  {addon.imageUrl ? (
                    <img
                      src={addon.imageUrl}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      className="aspect-square w-full bg-surface-sunken object-cover"
                    />
                  ) : (
                    <span className="block aspect-square w-full bg-surface-sunken" />
                  )}

                  <span
                    aria-hidden
                    className="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-pill bg-white/85 text-ink backdrop-blur-md"
                  >
                    <Plus className="size-3.5" />
                  </span>
                </span>

                <span className="mt-1.5 block truncate text-xs text-ink">
                  {addon.name}
                </span>
                <span className="block text-xs text-ink-muted" data-price>
                  {Number(addon.price) === 0 ? 'Ücretsiz' : `+${formatPrice(addon.price)}`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CouponField({ cart }: { cart: Cart }) {
  const [code, setCode] = useState('');
  const apply = useApplyCoupon();
  const remove = useRemoveCoupon();

  if (cart.coupon) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-sm bg-leaf-50 px-3.5 py-2.5">
        <span className="flex min-w-0 items-center gap-2 text-sm text-leaf-900">
          <Tag className="size-4 shrink-0" aria-hidden />
          <span className="truncate font-medium">{cart.coupon.code}</span>
        </span>

        <button
          type="button"
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
          className="flex size-7 shrink-0 items-center justify-center rounded-sm text-leaf-700 hover:bg-leaf-100"
          aria-label="Kuponu kaldır"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();

        if (code.trim()) {
          apply.mutate(code.trim(), { onSuccess: () => setCode('') });
        }
      }}
      className="flex items-end gap-2"
    >
      <Input
        label="İndirim kodu"
        optional
        value={code}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
        placeholder="Kodunuz"
        autoComplete="off"
      />

      <Button
        type="submit"
        variant="secondary"
        disabled={!code.trim()}
        isLoading={apply.isPending}
      >
        Uygula
      </Button>
    </form>
  );
}

function CartSummary({
  cart,
  onNavigate,
}: {
  cart: Cart;
  onNavigate?: () => void;
}) {
  const hasIssue = cart.items.some((item) => item.issue !== null);

  return (
    <div className="border-t border-line bg-surface px-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] pt-4 sm:px-5">
      <dl className="space-y-1.5 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-ink-muted">Ara toplam</dt>
          <dd className="text-ink" data-price>
            {formatPrice(cart.totals.subtotal)}
          </dd>
        </div>

        {Number(cart.totals.discount) > 0 ? (
          <div className="flex justify-between gap-4">
            <dt className="text-ink-muted">İndirim</dt>
            <dd className="text-leaf-700" data-price>
              −{formatPrice(cart.totals.discount)}
            </dd>
          </div>
        ) : null}

        <div className="flex justify-between gap-4">
          <dt className="text-ink-muted">Teslimat</dt>
          <dd className={cn('text-ink', cart.deliveryPending && 'text-ink-muted')}>
            {cart.deliveryPending ? (
              // Bilinmeyen ücret uydurulmaz.
              <span className="text-xs">Teslimat adımında hesaplanacak</span>
            ) : Number(cart.totals.deliveryFee) === 0 ? (
              'Ücretsiz'
            ) : (
              <span data-price>{formatPrice(cart.totals.deliveryFee)}</span>
            )}
          </dd>
        </div>
      </dl>

      {cart.freeDeliveryRemaining ? (
        <p className="mt-2.5 text-xs text-ink-muted">
          <span data-price>{formatPrice(cart.freeDeliveryRemaining)}</span> daha
          ekleyin, teslimat ücretsiz olsun.
        </p>
      ) : null}

      <div className="mt-3 flex items-baseline justify-between gap-4 border-t border-line pt-3">
        <span className="text-base text-ink">Toplam</span>
        <span className="text-xl font-medium text-ink" data-price>
          {formatPrice(cart.totals.total)}
        </span>
      </div>

      {!cart.meetsMinOrder && cart.minOrder ? (
        <p className="mt-2.5 text-xs text-danger">
          {cart.zone?.name} için minimum sipariş tutarı{' '}
          <span data-price>{formatPrice(cart.minOrder)}</span>.
        </p>
      ) : null}

      <div className="mt-4 flex items-center justify-between gap-3">
        <span className="text-xs text-ink-muted">
          {cart.zone ? cart.zone.name : 'Bölge seçilmedi'}
        </span>
        <ZonePicker
          currentZoneName={cart.zone?.name ?? '—'}
          currentZoneSlug={cart.zone?.slug ?? ''}
          label={cart.zone ? 'Değiştir' : 'Bölge seç'}
          triggerClassName="text-xs"
        />
      </div>

      <Button asChild block size="lg" className="mt-4" disabled={hasIssue}>
        <Link to="/odeme" onClick={onNavigate}>
          Siparişi tamamla
        </Link>
      </Button>

      {hasIssue ? (
        <p className="mt-2 text-center text-xs text-danger">
          Devam etmeden önce yukarıdaki uyarıları giderin.
        </p>
      ) : null}
    </div>
  );
}

function CartSkeleton() {
  return (
    <div className="p-4 sm:p-5">
      {Array.from({ length: 2 }, (_, index) => (
        <div key={index} className="flex gap-3.5 border-b border-line py-5">
          <Skeleton className="size-20 shrink-0 rounded-sm sm:size-24" />
          <div className="flex-1">
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="mt-2 h-3 w-1/3" />
            <Skeleton className="mt-4 h-8 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}
